import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import Layout from "./components/Layout";
import AuthPage from "./pages/AuthPage";
import HomePage from "./pages/HomePage";
import LeaderboardPage from "./pages/LeaderboardPage";
import ClansPage from "./pages/ClansPage";
import ClanDetailPage from "./pages/ClanDetailPage";
import MatchesPage from "./pages/MatchesPage";
import MatchDetailPage from "./pages/MatchDetailPage";
import PlayersPage from "./pages/PlayersPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import RulesPage from "./pages/RulesPage";
import TournamentsPage from "./pages/TournamentsPage";
import TournamentDetailPage from "./pages/TournamentDetailPage";
import BlacklistPage from "./pages/BlacklistPage";
import PersonalPlusPage from "./pages/PersonalPlusPage";
import PlayerProfilePage from "./pages/PlayerProfilePage";
import LeaguesPage from "./pages/LeaguesPage";
import LeagueDetailPage from "./pages/LeagueDetailPage";
import ChampionsFactoryPage from "./pages/ChampionsFactoryPage";
import NewsTransfersPage from "./pages/NewsTransfersPage";
import RivalsGuardPage from "./pages/RivalsGuardPage";
import OnboardingTour from "./components/OnboardingTour";
import SanadChatWidget from "./components/match/SanadChatWidget";
import { Toaster } from "sonner";
import LiveStreamsPage from "./pages/LiveStreamsPage";

const LAUNCH_TARGET_AT = (process.env.REACT_APP_LAUNCH_TARGET_AT || "2026-07-22T00:00:00+03:00").trim();
const LAUNCH_LOCK_ENABLED = ((process.env.REACT_APP_LAUNCH_LOCK_ENABLED || "true").trim().toLowerCase() !== "false");

function toCountdownParts(msLeft) {
  const total = Math.max(0, Math.floor(msLeft / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return { days, hours, minutes, seconds, done: total <= 0 };
}

function launchMomentSeenKey() {
  return `launch-moment-seen:${LAUNCH_TARGET_AT}`;
}

function hasSeenLaunchMoment() {
  try {
    return window.sessionStorage.getItem(launchMomentSeenKey()) === "1";
  } catch {
    return false;
  }
}

function markLaunchMomentSeen() {
  try {
    window.sessionStorage.setItem(launchMomentSeenKey(), "1");
  } catch {
    // ignore
  }
}

function LaunchLockScreen({ targetAt, onReachedZero }) {
  const targetMs = useMemo(() => new Date(targetAt).getTime(), [targetAt]);
  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const left = Number.isFinite(targetMs) ? Math.max(0, targetMs - nowMs) : 0;
  const parts = toCountdownParts(left);

  useEffect(() => {
    if (parts.done) onReachedZero();
  }, [parts.done, onReachedZero]);

  return (
    <div className="min-h-screen bg-[#05070d] text-white grid place-items-center px-5" dir="rtl">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(6,182,212,0.12),transparent_38%),radial-gradient(circle_at_80%_80%,rgba(168,85,247,0.12),transparent_42%)]" />
      <div className="relative z-10 w-full max-w-3xl mx-auto rounded-3xl border border-white/10 bg-[#0b0f17]/88 backdrop-blur-xl p-8 md:p-10 shadow-[0_30px_120px_rgba(0,0,0,0.62)]">
        <p className="mb-4 text-xs font-semibold tracking-[0.35em] text-white/45 text-center">LAUNCH LOCK</p>
        <h1 className="text-center text-4xl md:text-5xl font-black leading-tight">
          <span
            title="موعدنا 22 يوليو الساعة 12 الليل!"
            aria-label="موعدنا 22 يوليو الساعة 12 الليل!"
            className="inline-block align-middle drop-shadow-[0_0_12px_rgba(253,224,71,0.5)]"
          >
            🔥
          </span>{" "}
          <span className="bg-gradient-to-l from-white via-cyan-200 to-white bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(125,211,252,0.4)]">
            بداية عصر جديد للسنايبر
          </span>
        </h1>
        <p className="mt-3 text-center text-sm sm:text-base text-white/65">قاعدين نضبط لك منصة تليق بلعبك.. خلك جاهز.</p>

        <div className="mt-8 grid grid-cols-4 gap-4">
          {[
            { key: "days", label: "أيام", value: parts.days },
            { key: "hours", label: "ساعات", value: parts.hours },
            { key: "minutes", label: "دقايق", value: parts.minutes },
            { key: "seconds", label: "ثواني", value: parts.seconds },
          ].map((item) => (
            <div key={item.key} className="rounded-2xl border border-white/12 bg-white/[0.03] py-5 md:py-6 text-center shadow-[inset_0_0_22px_rgba(255,255,255,0.03)]">
              <div className="text-3xl md:text-4xl font-black tabular-nums">
                {item.key === "minutes" || item.key === "seconds"
                  ? String(item.value).padStart(2, "0")
                  : String(item.value)}
              </div>
              <div className="mt-1 text-xs md:text-sm text-white/55">{item.label}</div>
            </div>
          ))}
        </div>

        <p className="mt-9 text-center text-sm text-emerald-300/90 sm:text-base">موعدنا 22 يوليو، 12 الليل.. لا تسحب علينا! 🟢</p>
      </div>
    </div>
  );
}

function LaunchMomentOverlay({ onDone }) {
  const reducedMotion = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  const sparks = useMemo(
    () => Array.from({ length: reducedMotion ? 0 : 16 }, (_, i) => ({
      id: i,
      x: `${10 + ((i * 37) % 80)}%`,
      delay: `${(i % 9) * 0.18}s`,
      dur: `${2.4 + (i % 4) * 0.45}s`,
    })),
    [reducedMotion]
  );

  const autoCloseMs = reducedMotion ? 7000 : 12000;
  const [msLeft, setMsLeft] = useState(autoCloseMs);

  useEffect(() => {
    const tick = setInterval(() => {
      setMsLeft((prev) => Math.max(0, prev - 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const t = setTimeout(onDone, autoCloseMs);
    return () => clearTimeout(t);
  }, [onDone, autoCloseMs]);

  const openRoute = (path) => {
    onDone();
    window.location.href = path;
  };

  return (
    <div className="fixed inset-0 z-[1600] overflow-y-auto overscroll-contain" dir="rtl">
      <style>{`
        @keyframes launchPulse {
          0% { transform: scale(0.95); opacity: 0.35; }
          55% { transform: scale(1.06); opacity: 1; }
          100% { transform: scale(1.02); opacity: 0.75; }
        }
        @keyframes launchSpark {
          0% { transform: translateY(100vh) scale(0.7); opacity: 0; }
          20% { opacity: 1; }
          100% { transform: translateY(-18vh) scale(1.2); opacity: 0; }
        }
        @keyframes vipShimmer {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
      `}</style>

      <div className="absolute inset-0 bg-[#05070d]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(250,204,21,0.15),transparent_32%),radial-gradient(circle_at_20%_20%,rgba(34,211,238,0.12),transparent_30%),radial-gradient(circle_at_80%_80%,rgba(167,139,250,0.14),transparent_35%)]" />

      {sparks.map((s) => (
        <span
          key={s.id}
          className="absolute h-2 w-2 rounded-full bg-gradient-to-l from-yellow-300 to-cyan-300 blur-[0.5px] will-change-transform"
          style={{ left: s.x, bottom: "-12px", animation: `launchSpark ${s.dur} linear infinite`, animationDelay: s.delay }}
        />
      ))}

      <div className="relative z-10 min-h-full flex items-center justify-center px-3 py-6 text-center">
        <div className="w-full max-w-[900px] rounded-3xl border border-emerald-300/20 bg-[#0b1018]/70 backdrop-blur-xl p-4 sm:p-10 shadow-[0_35px_140px_rgba(0,0,0,0.65)]">
          <div className="mx-auto mb-3 inline-flex items-center gap-2 rounded-full border border-amber-300/35 bg-amber-300/10 px-4 py-1 text-[10px] tracking-[0.28em] text-amber-200">
            VIP LAUNCH EDITION ✦
          </div>

          <div className="mx-auto mb-5 h-20 w-20 rounded-full bg-emerald-400/18 border border-emerald-300/45 grid place-items-center text-4xl animate-[launchPulse_1.4s_ease-in-out_infinite]">
            🚀
          </div>

          <h2 className="text-3xl sm:text-6xl font-black leading-tight bg-gradient-to-l from-emerald-200 via-white to-cyan-200 bg-clip-text text-transparent drop-shadow-[0_0_24px_rgba(52,211,153,0.45)]">
            انطلقنا رسميًا!
          </h2>

          <p className="mt-4 text-base sm:text-xl text-white/80 leading-relaxed">
            أهلًا بك في Rivals — تجربة أقوى، سرعة أعلى، ومنافسة ترفع الأدرينالين.
          </p>

          <p className="mt-2 text-sm sm:text-base text-white/60">جاهز؟ هذه أسرع طريقة تبدأ فيها داخل المنصة 👇</p>

          <div className="mt-4 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.max(0, (msLeft / autoCloseMs) * 100)}%`,
                backgroundImage: "linear-gradient(90deg, rgba(52,211,153,0.95), rgba(34,211,238,0.9), rgba(251,191,36,0.9))",
                backgroundSize: "200% 100%",
                animation: "vipShimmer 1.6s linear infinite",
              }}
            />
          </div>

          <div className="mt-6 grid md:grid-cols-3 gap-3 text-right">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-[11px] uppercase tracking-widest text-cyan-200/85">الخطوة 1</div>
              <div className="mt-1 text-sm font-bold text-white">استكشف المباريات</div>
              <div className="mt-1 text-xs text-white/60">تابع المباشر، النتائج، وتفاصيل المواجهات.</div>
              <button onClick={() => openRoute("/matches")} className="mt-3 w-full rounded-lg bg-cyan-300/90 text-black text-sm font-black py-2 hover:bg-cyan-200">
                اذهب للمباريات
              </button>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-[11px] uppercase tracking-widest text-emerald-200/85">الخطوة 2</div>
              <div className="mt-1 text-sm font-bold text-white">ادخل عالم الكلانات</div>
              <div className="mt-1 text-xs text-white/60">أنشئ كلانك أو انضم لفريقك وابدأ التحدي.</div>
              <button onClick={() => openRoute("/clans")} className="mt-3 w-full rounded-lg bg-emerald-300/90 text-black text-sm font-black py-2 hover:bg-emerald-200">
                اذهب للكلانات
              </button>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-[11px] uppercase tracking-widest text-amber-200/85">الخطوة 3</div>
              <div className="mt-1 text-sm font-bold text-white">فعّل RivalsGuard</div>
              <div className="mt-1 text-xs text-white/60">تجربة احترافية وحماية تنافسية أثناء المباراة.</div>
              <button onClick={() => openRoute("/rivalsguard")} className="mt-3 w-full rounded-lg bg-amber-300/90 text-black text-sm font-black py-2 hover:bg-amber-200">
                اذهب لـ RivalsGuard
              </button>
            </div>
          </div>

          <div className="sticky bottom-0 z-20 mt-7 flex items-center justify-center gap-3 flex-wrap rounded-xl bg-[#0b1018] py-3">
            <button
              onClick={() => {
                onDone();
                window.location.replace("/");
              }}
              className="px-6 py-3 rounded-xl bg-emerald-400 text-black font-black hover:bg-emerald-300 shadow-[0_0_28px_rgba(52,211,153,0.45)]"
            >
              ادخل المنصة الآن
            </button>

            <span className="text-xs text-white/45">إغلاق تلقائي خلال {Math.ceil(msLeft / 1000)}ث</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();

  if (loading) return <div className="min-h-screen grid place-items-center text-white/40">...</div>;

  if (!user) return <Navigate to="/auth" replace />;

  return children;
}

function PublicShell({ children }) {
  const { loading } = useAuth();

  if (loading) return <div className="min-h-screen grid place-items-center text-white/40">...</div>;

  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const location = useLocation();
  const routeMatch = location.pathname.match(/^\/matches\/([^/]+)/);
  const activeMatchId = routeMatch ? routeMatch[1] : "";

  return (
    <>
      <Routes>
        <Route path="/auth" element={<AuthPage />} />

        <Route path="/" element={<PublicShell><HomePage /></PublicShell>} />
        <Route path="/clans" element={<PublicShell><ClansPage /></PublicShell>} />
        <Route path="/clans/:id" element={<PublicShell><ClanDetailPage /></PublicShell>} />
        <Route path="/matches" element={<PublicShell><MatchesPage /></PublicShell>} />
        <Route path="/players" element={<PublicShell><PlayersPage /></PublicShell>} />
        <Route path="/players/:id" element={<PublicShell><PlayerProfilePage /></PublicShell>} />

        <Route
          path="/leaderboard"
          element={<PublicShell><LeaderboardPage /></PublicShell>}
        />

        <Route path="/leagues" element={<PublicShell><LeaguesPage /></PublicShell>} />
        <Route path="/leagues/:id" element={<PublicShell><LeagueDetailPage /></PublicShell>} />
        <Route path="/champions" element={<PublicShell><ChampionsFactoryPage /></PublicShell>} />
        <Route path="/champions-factory" element={<PublicShell><ChampionsFactoryPage /></PublicShell>} />
        <Route path="/news" element={<PublicShell><NewsTransfersPage /></PublicShell>} />
        <Route path="/rivalsguard" element={<PublicShell><RivalsGuardPage /></PublicShell>} />
        <Route path="/rules" element={<PublicShell><RulesPage /></PublicShell>} />
        <Route path="/tournaments" element={<PublicShell><TournamentsPage /></PublicShell>} />
        <Route path="/tournaments/:id" element={<PublicShell><TournamentDetailPage /></PublicShell>} />
        <Route path="/blacklist" element={<PublicShell><BlacklistPage /></PublicShell>} />
        <Route path="/plus" element={<PublicShell><PersonalPlusPage /></PublicShell>} />
        <Route path="/streams" element={<PublicShell><LiveStreamsPage /></PublicShell>} />

        <Route
          path="/matches/:id"
          element={
            <Protected>
              <Layout>
                <MatchDetailPage />
              </Layout>
            </Protected>
          }
        />

        <Route
          path="/me"
          element={
            <Protected>
              <Layout>
                <ProfilePage />
              </Layout>
            </Protected>
          }
        />

        <Route
          path="/admin"
          element={
            <Protected>
              <Layout>
                <AdminPage />
              </Layout>
            </Protected>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <SanadChatWidget
        matchId={activeMatchId}
        disabled={false}
      />
    </>
  );
}

export default function App() {
  const targetMs = useMemo(
    () => new Date(LAUNCH_TARGET_AT).getTime(),
    []
  );

  const launchStarted =
    Number.isFinite(targetMs) && Date.now() >= targetMs;

  const [launchUnlocked, setLaunchUnlocked] = useState(
    () => !LAUNCH_LOCK_ENABLED || launchStarted
  );

  const [showLaunchMoment, setShowLaunchMoment] = useState(
    () => launchStarted && !hasSeenLaunchMoment()
  );

  useEffect(() => {
    if (!LAUNCH_LOCK_ENABLED) {
      setLaunchUnlocked(true);
      setShowLaunchMoment(false);
      return;
    }

    if (launchStarted) {
      setLaunchUnlocked(true);

      if (!hasSeenLaunchMoment()) {
        setShowLaunchMoment(true);
        markLaunchMomentSeen();
      }
    }
  }, [launchStarted]);

  const unlockNow = () => {
    setLaunchUnlocked(true);

    if (!hasSeenLaunchMoment()) {
      setShowLaunchMoment(true);
      markLaunchMomentSeen();
    }
  };

  if (LAUNCH_LOCK_ENABLED && !launchUnlocked) {
    return (
      <LaunchLockScreen
        targetAt={LAUNCH_TARGET_AT}
        onReachedZero={unlockNow}
      />
    );
  }

  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <OnboardingTour />
        <Toaster
          position="top-center"
          theme="dark"
          richColors
        />
      </BrowserRouter>

      {showLaunchMoment && (
        <LaunchMomentOverlay
          onDone={() => setShowLaunchMoment(false)}
        />
      )}
    </AuthProvider>
  );
}
